import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, Avatar, Checkbox, Button } from '@mui/material';
import { MoreHorizIcon } from 'icons/materialIcons';
import avatarPlaceholder from 'images/avatar-cropped.png';
import TeamMemberTemplateButton from './TeamMemberTemplateButton';
import TeamMemberSubteamButton from './TeamMemberSubteamButton';
import TeamMemberCardOptionsModal from './TeamMemberCardOptionsModal';
import EditAdminStatusModal from './EditAdminStatusModal';
import TeamNotificationSnackbar from 'team/shared/components/notification/TeamNotificationSnackbar';
import { useTeam } from 'team/contexts/teamContext';
import { useAuth } from 'contexts/AuthContext';
import { downloadQRCode } from 'apis/qrCodeApi';
import { updateTeamMemberRole } from 'team/apis/teamApi';

const TeamMemberListItem = ({
    member,
    isSelected,
    onSelectionChange,
    onEditCardClick,
    teamId,
    index,
    isLast
}) => {
    const { authState } = useAuth();
    const { currentTeamId, teams } = useTeam();
    const currentTeam = teams[currentTeamId];
    const isCurrentUserAdmin = currentTeam?.membershipInfo?.role === 'admin' || currentTeam?.membershipInfo?.role === 'owner' || currentTeam?.membershipInfo?.role === 'teamOwner' || currentTeam?.membershipInfo?.role === 'team_owner';
    
    const [isOptionsModalOpen, setIsOptionsModalOpen] = useState(false);
    const [isEditAdminModalOpen, setIsEditAdminModalOpen] = useState(false);
    const [memberRole, setMemberRole] = useState(member?.role || 'member');
    const [notification, setNotification] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    useEffect(() => {
        if (member?.role) {
            setMemberRole(member.role);
        }
    }, [member?.role]);
    
    const optionsButtonRef = useRef(null);
    const secondaryTextColor = '#666';

    const memberCardId = member?.defaultCard?._id;

    const handleMoreMenuClick = (event) => {
        event.stopPropagation();
        setIsOptionsModalOpen(true);
    };

    const handleOptionsModalClose = () => {
        setIsOptionsModalOpen(false);
    };

    const handleEditAdminStatus = () => {
        setIsEditAdminModalOpen(true);
        setIsOptionsModalOpen(false);
    };

    const handleDownloadQrCode = async () => {
        if (!memberCardId) {
            setNotification({
                open: true,
                message: 'No default card found for this member',
                severity: 'error'
            });
            return;
        }

        try {
            const cardUrl = `${window.location.origin}/card/${memberCardId}?source=qr_code`;
            await downloadQRCode(cardUrl);
            setIsOptionsModalOpen(false);
        } catch (error) {
            console.error('Error downloading QR code:', error);
            setNotification({
                open: true,
                message: 'Failed to download QR code',
                severity: 'error'
            });
        }
    };

    const handleViewCard = () => {
        if (!memberCardId) {
            setNotification({
                open: true,
                message: 'No default card found for this member',
                severity: 'error'
            });
            return;
        }

        const cardUrl = `${window.location.origin}/card/${memberCardId}?source=team_member_card`;
        window.open(cardUrl, '_blank', 'noopener,noreferrer');
        setIsOptionsModalOpen(false);
    };

    const handleApplyRole = async (newRole) => {
        try {
            const teamIdToUse = teamId || currentTeamId;
            const result = await updateTeamMemberRole(teamIdToUse, member.userId, newRole);

            if (result.success) {
                setNotification({
                    open: true,
                    message: `Member role updated to ${newRole} successfully`,
                    severity: 'success'
                });
                
                member.role = newRole;
                setMemberRole(newRole);
            }
        } catch (error) {
            setNotification({
                open: true,
                message: error.message || 'Failed to update member role',
                severity: 'error'
            });
        }
    };

    const getMemberName = (m) => {
        return (
            m.defaultCard?.contact_info?.fullName ||
            m.defaultCard?.cardName ||
            m.user?.fullName ||
            m.contact_info?.fullName ||
            m.cardName ||
            m.fullName ||
            'Unknown User'
        );
    };

    const getJobTitle = (m) => {
        return (
            m.defaultCard?.contact_info?.jobTitle ||
            m.contact_info?.jobTitle ||
            ''
        );
    };

    const getEmail = (m) => {
        if (m?.user?.isCompanyPage) {
            return 'Company Page';
        }
        return (
            m.defaultCard?.contact_info?.email ||
            m.user?.email ||
            m.email ||
            ''
        );
    };

    const getMemberRoleLabelValue = () => {
        const role = memberRole;
        const isAdmin =
            role === 'admin' ||
            role === 'team_admin' ||
            role === 'owner' ||
            role === 'teamOwner' ||
            member?.isTeamAdmin;
        return isAdmin ? 'TEAM ADMIN' : '';
    };

    const handleRowClick = (event) => {
        if (event.target.closest('.MuiCheckbox-root')) return;
        if (event.target.closest('[data-member-action]')) return;
        if (event.target.closest('.MuiButton-root')) return;
        onEditCardClick(member);
    };

    const fullName = getMemberName(member);
    const jobTitle = getJobTitle(member);
    const email = getEmail(member);
    const roleLabelValue = getMemberRoleLabelValue();
    const profilePictureUrl =
        member.defaultCard?.profilePictureUrl ||
        member.user?.profilePictureUrl ||
        null;

    return (
        <>
            <Box
                onClick={handleRowClick}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0.75rem 1rem',
                    backgroundColor: '#fff',
                    cursor: 'pointer',
                    borderBottom: isLast ? 'none' : '1px solid #f0f0f0',
                    '&:hover': {
                        backgroundColor: '#f8f9fa'
                    }
                }}
            >
                <Box sx={{ width: '3rem', display: 'flex', justifyContent: 'center' }}>
                    <Checkbox
                        checked={isSelected}
                        onChange={(e) => onSelectionChange(e.target.checked)}
                        sx={{
                            color: '#d0d0d0',
                            padding: 0,
                            '&.Mui-checked': {
                                color: '#a0a0a0'
                            },
                            '& .MuiSvgIcon-root': {
                                borderRadius: '0.5rem',
                                backgroundColor: '#fff'
                            }
                        }}
                    />
                </Box>
                <Box sx={{ width: '4.5rem', display: 'flex', justifyContent: 'center' }}>
                    <Avatar
                        src={profilePictureUrl || avatarPlaceholder}
                        sx={{ width: 48, height: 48 }}
                    />
                </Box>
                <Box sx={{ flex: '1 1 12rem', minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0 }}>
                        <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 600, color: '#111', fontSize: '1rem' }}
                            noWrap
                        >
                            {fullName}
                        </Typography>
                        {roleLabelValue && (
                            <Box
                                sx={{
                                    backgroundColor: '#f7f7f7',
                                    color: '#949494',
                                    borderRadius: '999px',
                                    padding: '0.125rem 0.5rem',
                                    fontSize: '0.7rem',
                                    fontWeight: 600,
                                    textTransform: 'uppercase',
                                    flexShrink: 0
                                }}
                            >
                                {roleLabelValue}
                            </Box>
                        )}
                    </Box>
                    {jobTitle && (
                        <Typography
                            variant="body2"
                            sx={{ color: secondaryTextColor, fontSize: '0.85rem', fontWeight: 500 }}
                            noWrap
                        >
                            {jobTitle}
                        </Typography>
                    )}
                </Box>
                <Box sx={{ flex: '1 1 10rem', minWidth: 0 }}>
                    <Typography
                        variant="body2"
                        sx={{ color: secondaryTextColor, fontSize: '0.85rem', fontWeight: 500 }}
                        noWrap
                    >
                        {email || '—'}
                    </Typography>
                </Box>
                <Box sx={{ flex: '0 0 12rem', minWidth: 0, display: 'flex', justifyContent: 'center' }}>
                    <TeamMemberTemplateButton member={member} teamId={teamId} />
                </Box>
                <Box sx={{ flex: '0 0 12rem', minWidth: 0, display: 'flex', justifyContent: 'center' }}>
                    <TeamMemberSubteamButton member={member} teamId={teamId} />
                </Box>
                <Box sx={{ width: '4rem', display: 'flex', justifyContent: 'center' }}>
                    <Button
                        ref={optionsButtonRef}
                        onClick={handleMoreMenuClick}
                        data-member-action="true"
                        sx={{
                            minWidth: '2rem',
                            height: '2rem',
                            padding: '0',
                            borderRadius: '999px',
                            color: '#4f4f4f',
                            '&:hover': {
                                backgroundColor: '#f0f0f0',
                            },
                        }}
                    >
                        <MoreHorizIcon sx={{ fontSize: '1.25rem' }} />
                    </Button>
                </Box>
            </Box>

            <TeamMemberCardOptionsModal
                open={isOptionsModalOpen}
                onClose={handleOptionsModalClose}
                anchorEl={optionsButtonRef.current}
                onDownloadQrCode={handleDownloadQrCode}
                onViewCard={handleViewCard}
                onEditAdminStatus={handleEditAdminStatus}
                showAdminRole={isCurrentUserAdmin && !(
                    memberRole === 'owner' || 
                    memberRole === 'teamOwner' || 
                    memberRole === 'team_owner' || 
                    member?.isOwner || 
                    member?.isTeamOwner || 
                    member?.membershipInfo?.role === 'owner' || 
                    member?.membershipInfo?.role === 'teamOwner' || 
                    member?.user?.role === 'owner' || 
                    member?.user?.role === 'teamOwner' || 
                    member?.user?.role === 'team_owner' || 
                    member?.userId === authState?.user?.userId ||
                    member?.userId === currentTeam?.membershipInfo?.userId || 
                    member?.userId === teams[currentTeamId]?.membershipInfo?.userId ||
                    member?.userId === currentTeam?.userId ||
                    member?.userId === teams[teamId]?.userId
                )}
            />

            <EditAdminStatusModal
                open={isEditAdminModalOpen}
                onClose={() => setIsEditAdminModalOpen(false)}
                member={member}
                onApply={handleApplyRole}
            />

            <TeamNotificationSnackbar
                open={notification.open}
                message={notification.message}
                handleClose={() => setNotification(prev => ({ ...prev, open: false }))}
                severity={notification.severity}
            />
        </>
    );
};

export default TeamMemberListItem;
